// src/components/eventos/views/EventosOperacional.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { useEventos } from '../hooks/useEventos';
import { useTempoAtual } from '../hooks/useTarefaTempo';
import EventoAtivoCard from '../components/operacional/EventoAtivoCard';
import ProximoEventoCard from '../components/operacional/ProximoEventoCard';
import TarefaCard from '../components/operacional/TarefaCard';
import EstadoVazio from '../components/operacional/EstadoVazio';
import EventoRevisao from '../EventoRevisao';

/* ---- Ícones SVG ---- */
const ArrowLeftIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
);
const CogsIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);
const WarningIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);
const LogoutIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
);
const ChevronIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const EventosOperacional = ({ supabase, session, clienteData, onLogout }) => {
    const navigate = useNavigate();
    const toast = useToast();
    const activeTaskRef = useRef(null);
    const [isRevisaoOpen, setIsRevisaoOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const { eventoAtivo, proximoEvento, isLoading, error, refetch } = useEventos(supabase, session);
    const tempoAtual = useTempoAtual();

    const nomeUsuario = session?.user?.user_metadata?.nome || 'Usuário';
    const iniciais = nomeUsuario.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();

    const handleLogout = async () => { try { await onLogout(); } catch (e) { console.error(e); } };

    const adicionarMinutos = (hora, minutos) => {
        const [h, m] = hora.split(':').map(Number);
        const totalMinutos = h * 60 + m + minutos;
        const novaHora = Math.floor(totalMinutos / 60) % 24;
        const novoMinuto = totalMinutos % 60;
        return `${novaHora.toString().padStart(2,'0')}:${novoMinuto.toString().padStart(2,'0')}`;
    };

    const handleIniciarEvento = async (evento) => {
        try {
            const agora = new Date();
            const hoje = `${agora.getFullYear()}-${String(agora.getMonth()+1).padStart(2,'0')}-${String(agora.getDate()).padStart(2,'0')}`;
            const eventoData = evento.data?.split('T')[0] || evento.data;
            const primeiraHora = evento.tarefas?.[0]?.horaInicio;
            const horaAtual = `${agora.getHours().toString().padStart(2,'0')}:${agora.getMinutes().toString().padStart(2,'0')}`;
            if (eventoData > hoje || (eventoData === hoje && primeiraHora && horaAtual < primeiraHora)) {
                const msg = eventoData > hoje
                    ? `Este evento está agendado para ${eventoData.split('-').reverse().join('/')}${primeiraHora ? ` às ${primeiraHora}` : ''}.`
                    : `Este evento começa às ${primeiraHora}.`;
                toast({ title: 'Ainda não é hora', description: msg, status: 'warning', duration: 4000, isClosable: true });
                return;
            }
            const horaInicioReal = agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            const novasTarefas = (evento.tarefas || []).map((t, idx) => ({
                ...t,
                status: idx === 0 ? 'em_andamento' : 'pendente',
                horaInicioReal: idx === 0 ? horaInicioReal : null,
            }));
            const { error } = await supabase
                .from('eventos')
                .update({ status: 'em_andamento', hora_inicio_real: horaInicioReal, tarefas: novasTarefas })
                .eq('id', evento.id);
            if (error) throw error;
            toast({ title: 'Evento iniciado!', description: 'Primeira tarefa em andamento.', status: 'success', duration: 3000, isClosable: true });
            refetch();
        } catch (error) {
            toast({ title: 'Erro ao iniciar evento', description: error.message, status: 'error', duration: 5000, isClosable: true });
        }
    };

    const handleConcluirTarefa = async (tarefaId) => {
        if (!eventoAtivo) return;
        try {
            const agora = new Date();
            const horaAtual = agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            const tarefaIdx = eventoAtivo.tarefas.findIndex(t => t.id === tarefaId);
            const novasTarefas = [...eventoAtivo.tarefas];
            novasTarefas[tarefaIdx] = { ...novasTarefas[tarefaIdx], status: 'concluida', horaFimReal: horaAtual };
            for (let i = tarefaIdx + 1; i < novasTarefas.length; i++) {
                if (novasTarefas[i].status === 'pendente') {
                    const duracaoTarefa = novasTarefas[i].duracao;
                    const novaHoraInicio = i === tarefaIdx + 1 ? horaAtual : novasTarefas[i-1].horaFim;
                    const novaHoraFim = adicionarMinutos(novaHoraInicio, duracaoTarefa);
                    novasTarefas[i] = { ...novasTarefas[i], horaInicio: novaHoraInicio, horaFim: novaHoraFim, status: i === tarefaIdx + 1 ? 'em_andamento' : 'pendente', horaInicioReal: i === tarefaIdx + 1 ? horaAtual : null };
                }
            }
            const todasConcluidas = novasTarefas.every(t => t.status === 'concluida');
            const novoStatus = todasConcluidas ? 'concluido' : 'em_andamento';
            const { error } = await supabase.from('eventos').update({ tarefas: novasTarefas, status: novoStatus, hora_fim_real: todasConcluidas ? horaAtual : null }).eq('id', eventoAtivo.id);
            if (error) throw error;
            toast({ title: 'Tarefa concluída!', description: todasConcluidas ? 'Evento finalizado!' : 'Próxima tarefa iniciada.', status: 'success', duration: 3000, isClosable: true });
            refetch();
        } catch (error) {
            toast({ title: 'Erro ao concluir tarefa', description: error.message, status: 'error', duration: 5000, isClosable: true });
        }
    };

    // Scroll para tarefa ativa a cada refresh
    useEffect(() => {
        if (isLoading || !activeTaskRef.current) return;
        activeTaskRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [eventoAtivo, isLoading]);

    const calcularTempoTarefa = (tarefa) => {
        const horaStr = tarefa.horaInicioReal || tarefa.horaInicio;
        if (!horaStr || !horaStr.includes(':')) {
            const duracao = (tarefa.duracao || 0) * 60;
            return { decorrido: 0, restante: duracao, duracao };
        }
        const agora = tempoAtual;
        const [h, m] = horaStr.split(':').map(Number);
        const inicio = new Date(agora);
        inicio.setHours(h, m, 0);
        const decorrido = Math.floor((agora - inicio) / 1000);
        const duracao = (tarefa.duracao || 0) * 60;
        return { decorrido, restante: duracao - decorrido, duracao };
    };

    useEffect(() => {
        if (!menuOpen) return;
        const close = () => setMenuOpen(false);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [menuOpen]);

    if (error) {
        return (
            <div style={{ minHeight:'100vh', background:'#f0faf6', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Inter', -apple-system, sans-serif" }}>
                <div style={{ textAlign:'center' }}>
                    <div style={{ color:'rgba(239,68,68,0.5)', margin:'0 auto 16px', width:'fit-content' }}><WarningIcon /></div>
                    <p style={{ margin:'0 0 6px', fontSize:'18px', fontWeight:600, color:'#ef4444' }}>{error}</p>
                    <p style={{ margin:0, fontSize:'14px', color:'#94a3b8' }}>Verifique sua conexão e tente novamente</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }

                .eo-menu-item { display:flex; align-items:center; gap:10px; width:100%; padding:10px 14px; border-radius:10px; font-size:13px; font-weight:500; color:#64748b; background:none; border:none; cursor:pointer; font-family:inherit; transition:all 0.15s; text-align:left; }
                .eo-menu-item:hover { background:#f8fafc; color:#1e293b; }
                .eo-menu-item.danger { color:#ef4444; }
                .eo-menu-item.danger:hover { background:rgba(239,68,68,0.06); }
                @media (max-width: 640px) { .eo-nav-email { display:none; } }
            `}</style>

            <div style={{ minHeight:'100vh', background:'#f0faf6', fontFamily:"'Inter', -apple-system, sans-serif" }}>

                {/* NAVBAR */}
                <header style={{ position:'sticky', top:0, zIndex:50, background:'rgba(255,255,255,0.9)', backdropFilter:'blur(24px)', WebkitBackdropFilter:'blur(24px)', borderBottom:'1px solid #e2e8f0' }}>
                    <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 24px', height:'64px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'16px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
                            <div style={{ display:'inline-flex', alignItems:'center', background:'#ffffff', borderRadius:'10px', padding:'6px 14px', boxShadow:'0 2px 8px rgba(0,0,0,0.08)', border:'1px solid #e2e8f0' }}>
                                <img src="/logo-horizontal.png" alt="Evlo" style={{ height:'26px', objectFit:'contain' }} onError={e => { e.target.style.display='none'; }} />
                            </div>
                            <button onClick={() => navigate('/eventos')} style={{ display:'flex', alignItems:'center', gap:'6px', padding:'7px 14px', borderRadius:'10px', background:'#f8fafc', border:'1px solid #e2e8f0', color:'#64748b', fontSize:'13px', fontWeight:500, cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s', whiteSpace:'nowrap' }}
                                onMouseEnter={e => e.currentTarget.style.background='#f0fdf8'}
                                onMouseLeave={e => e.currentTarget.style.background='#f8fafc'}
                            >
                                <ArrowLeftIcon /> Voltar aos Eventos
                            </button>
                            {clienteData && (
                                <span style={{ padding:'4px 12px', borderRadius:'100px', background:'rgba(44,203,144,0.12)', border:'1px solid rgba(44,203,144,0.2)', color:'#2CCB90', fontSize:'11px', fontWeight:700 }}>
                                    {clienteData.nome}
                                </span>
                            )}
                        </div>

                        <div style={{ position:'relative' }}>
                            <button
                                onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
                                style={{ display:'flex', alignItems:'center', gap:'10px', padding:'6px 12px', borderRadius:'12px', background: menuOpen ? '#f8fafc' : '#ffffff', border:'1px solid #e2e8f0', cursor:'pointer', transition:'all 0.15s', fontFamily:'inherit' }}
                            >
                                <div style={{ width:'32px', height:'32px', borderRadius:'50%', background:'linear-gradient(135deg, #2CCB90, #2DB8E1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px', fontWeight:700, color:'#fff', flexShrink:0 }}>
                                    {iniciais}
                                </div>
                                <div className="eo-nav-email"><p style={{ margin:0, fontSize:'13px', fontWeight:600, color:'#0f172a' }}>{nomeUsuario}</p></div>
                                <span style={{ color:'#94a3b8', transition:'transform 0.2s', transform: menuOpen ? 'rotate(180deg)' : 'none' }}><ChevronIcon /></span>
                            </button>
                            {menuOpen && (
                                <div style={{ position:'absolute', top:'calc(100% + 8px)', right:0, minWidth:'190px', background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:'14px', boxShadow:'0 16px 48px rgba(0,0,0,0.12)', padding:'8px', zIndex:100 }}>
                                    <button className="eo-menu-item" onClick={() => { navigate('/perfil'); setMenuOpen(false); }}>Meu Perfil</button>
                                    <div style={{ height:'1px', background:'#e2e8f0', margin:'6px 0' }} />
                                    <button className="eo-menu-item danger" onClick={handleLogout}><LogoutIcon /> Sair</button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {/* CONTEÚDO */}
                <main style={{ position:'relative', zIndex:1, maxWidth:'1280px', margin:'0 auto', padding:'32px 24px 80px' }}>

                    {/* Header */}
                    <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'28px' }}>
                        <div style={{ width:'40px', height:'40px', borderRadius:'12px', background:'linear-gradient(135deg, rgba(37,99,235,0.12), rgba(45,184,225,0.12))', border:'1px solid rgba(37,99,235,0.15)', display:'flex', alignItems:'center', justifyContent:'center', color:'#2563eb' }}>
                            <CogsIcon />
                        </div>
                        <div>
                            <h1 style={{ margin:0, fontSize:'clamp(20px,3vw,28px)', fontWeight:800, color:'#0f172a', letterSpacing:'-0.02em' }}>Visão Operacional</h1>
                            <p style={{ margin:0, fontSize:'13px', color:'#64748b' }}>Acompanhamento em tempo real dos eventos</p>
                        </div>
                    </div>

                    {/* Loading */}
                    {isLoading ? (
                        <div style={{ textAlign:'center', padding:'80px 0' }}>
                            <div style={{ width:'48px', height:'48px', borderRadius:'50%', border:'3px solid rgba(44,203,144,0.2)', borderTopColor:'#2CCB90', animation:'spin 0.8s linear infinite', margin:'0 auto 16px' }} />
                            <p style={{ color:'#94a3b8', fontSize:'14px', margin:0 }}>Carregando...</p>
                        </div>
                    ) : !eventoAtivo ? (
                        /* Sem evento ativo */
                        proximoEvento ? (
                            <ProximoEventoCard evento={proximoEvento} onVerPrograma={() => setIsRevisaoOpen(true)} />
                        ) : (
                            <EstadoVazio />
                        )
                    ) : (
                        /* Evento ativo */
                        <>
                            <EventoAtivoCard evento={eventoAtivo} />

                            <div style={{ display:'flex', flexDirection:'column', gap:'12px', marginTop:'12px' }}>
                                {eventoAtivo.tarefas?.map((tarefa, idx) => {
                                    const tempoInfo = calcularTempoTarefa(tarefa);
                                    const emAndamento = tarefa.status === 'em_andamento';
                                    return (
                                        <TarefaCard
                                            key={tarefa.id}
                                            tarefa={tarefa}
                                            numero={idx + 1}
                                            tempoInfo={tempoInfo}
                                            tarefaRef={emAndamento ? activeTaskRef : null}
                                        />
                                    );
                                })}
                            </div>
                        </>
                    )}
                </main>
            </div>

            <EventoRevisao isOpen={isRevisaoOpen} onClose={() => setIsRevisaoOpen(false)} evento={proximoEvento} onIniciarEvento={handleIniciarEvento} />
        </>
    );
};

export default EventosOperacional;
